/**
 * WEB sheet: a centred dialog in a transparent `Modal` (react-native-web implements it), over a
 * backdrop that closes it on a click. Escape closes it through `onRequestClose`. `presentation` and
 * `wrapContent` are a phone's concern and are ignored here.
 *
 * `showClose`: the dialog clips its content (`overflow: "hidden"`, for its rounded corners), so a ✕
 * outside it cannot be the dialog's child. An unclipped ANCHOR takes the dialog's place and box, the
 * dialog fills it, and the ✕ hangs above the anchor's top-end corner.
 */
import React from "react";
import { Modal, Pressable, StyleSheet, View } from "react-native";

import { IconButton } from "@rista/ui/library/components/IconButton";
import { MODAL_ORIENTATIONS } from "@rista/ui/library/components/ModalOrientations";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { DIALOG_SIZES, SheetHeader, backdropTestID, closeTestID, sheetStyles, type SheetProps } from "./shared";

export * from "./shared";

export function Sheet({
  visible,
  onClose,
  closeLabel,
  title,
  subtitle,
  testID,
  role = "dialog",
  size = "wide",
  appearance,
  showClose,
  children,
}: SheetProps) {
  const { frame, chrome } = sheetStyles(useTheme(), appearance);
  const dialog = (
    <View role={role} aria-modal style={[frame.dialog, DIALOG_SIZES[size], showClose && styles.fillAnchor]} testID={testID}>
      <SheetHeader title={title} subtitle={subtitle} chrome={chrome} />
      {children}
    </View>
  );
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      supportedOrientations={MODAL_ORIENTATIONS}
      onRequestClose={onClose}
    >
      <View style={frame.scrim}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityLabel={closeLabel}
          testID={backdropTestID(testID)}
        />
        {showClose ? (
          <View style={[styles.anchor, DIALOG_SIZES[size], styles.unpainted]}>
            {dialog}
            <View style={styles.close}>
              <IconButton icon="close" variant="secondary" size="sm" label={closeLabel} onPress={onClose} testID={closeTestID(testID)} />
            </View>
          </View>
        ) : (
          dialog
        )}
      </View>
    </Modal>
  );
}

const CLOSE_GAP = 8;
/** the `sm` IconButton's height */
const CLOSE_SIZE = 36;

const styles = StyleSheet.create({
  // the dialog's placement (`look.ts` › `dialog`), on the unclipped anchor
  anchor: { alignSelf: "center", marginTop: "auto", marginBottom: "auto", maxHeight: "85%" },
  // the anchor takes the size's box, never its paint
  unpainted: { borderRadius: 13, boxShadow: "none" },
  // the dialog inside the anchor: its whole box, shrinking to the anchor's height cap
  fillAnchor: { width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%", marginTop: 0, marginBottom: 0, flexShrink: 1 },
  close: { position: "absolute", top: -(CLOSE_SIZE + CLOSE_GAP), right:"-45px",  end: 0 },
});

export default Sheet;
